import './layout.scss';

import { ConfigProvider } from 'antd';
import { Suspense, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useSearchParams } from 'react-router';

import {
  CLIENT_ROUTES,
  getFullClientRoute
} from '@/components/Shared/routes/routes';
import ErrorTemplate from '@/components/Shared/widgets/errors/error-template';
import { handleError } from '@/services/handleError';
import { useAppDispatch, useAppSelector } from '@/store';

import { ClientContext } from '../client.context';
import { getOnlineDictionaries, setClientCity } from '../client.store';
import { getClientAntdTheme } from '../shared/config/antd-theme';
import { setNotifyTheme } from '../shared/config/notify-theme';
import { ClientModuleLoader } from '../shared/ui';
import { isInWebView } from '../shared/webview/env';
import { ContinueRequest } from './entities';
import { BottomTabBar, Header } from './widgets';

const parseMultiBrands = (multiBrands: string | null): string => {
  if (!multiBrands) return '';
  try {
    return multiBrands.split(',').join(',');
  } catch (error) {
    handleError(error, { notify: false });
    return '';
  }
};

const ClientLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const { fullscreenCount, fullscreenBlurCount, layoutCount, layoutBlurCount } =
    useAppSelector((state) => state.loading);
  const { theme } = useAppSelector((state) => state.client);

  const [searchParams, setSearchParams] = useSearchParams();
  const filteredQrBrand = searchParams.get('filteredQrBrand');
  const filteredQrMultiBrands = parseMultiBrands(
    searchParams.get('filteredQrMultiBrands')
  );
  const filteredQrCity = searchParams.get('filteredQrCity');
  const filteredQrDealer = searchParams.get('filteredQrDealer');
  const dealer = searchParams.get('dealer');
  const city = searchParams.get('city');

  const [dictsLoading, setDictsLoading] = useState(true);

  const hasFullscreenLoading: boolean =
    fullscreenCount > 0 || fullscreenBlurCount > 0;

  const filteredQrMultiBrandsArray: string[] | null = useMemo(() => {
    if (!filteredQrMultiBrands) return null;
    return filteredQrMultiBrands.split(',');
  }, [filteredQrMultiBrands]);

  // Тема antd-компонентов клиента: акцент-цвета + dark-алгоритм в нативной dark.
  const antdThemeConfig = useMemo(
    () => getClientAntdTheme(theme, isInWebView()),
    [theme]
  );

  useEffect(() => {
    dispatch(getOnlineDictionaries()).finally(() => {
      setDictsLoading(false);
    });
  }, []);

  useEffect(() => {
    document.body.style.backgroundColor = '#e4e4e7';
    return () => {
      document.body.style.backgroundColor = 'initial';
    };
  }, []);

  useLayoutEffect(() => {
    // Для актуальных параметров используем window.location.search, а не searchParams
    const params = new URLSearchParams(window.location.search);

    if (filteredQrMultiBrandsArray && filteredQrMultiBrandsArray.length === 1) {
      params.set('brand', filteredQrMultiBrandsArray[0]);
    } else if (filteredQrBrand) {
      params.set('brand', filteredQrBrand);
    }
    if (filteredQrCity) {
      params.set('city', filteredQrCity);
    }
    if (filteredQrDealer) {
      params.set('dealer', filteredQrDealer);
    }
    setSearchParams(params, { replace: true });
  }, [
    filteredQrBrand,
    filteredQrMultiBrands,
    filteredQrCity,
    filteredQrDealer
  ]);

  useEffect(() => {
    // В webview город приходит из нативки (Redux), URL не задействован —
    // иначе пустой city-параметр обнулил бы выбранный город.
    if (isInWebView()) return;
    if (city) {
      try {
        sessionStorage.setItem('client-city', city);
      } catch (error) {
        handleError(error, { notify: false });
      }
    } else {
      dispatch(setClientCity(null));
    }
  }, [city]);

  // Нативную тему вешаем на корень документа (<html>), а не на .client-layout,
  // чтобы токены доставали и до контента в порталах (модалки AntD рендерятся
  // в body, вне .client-layout). Только для webview-режима.
  useEffect(() => {
    if (!isInWebView()) return;
    document.documentElement.classList.add('client-layout--native');

    return () => {
      document.documentElement.classList.remove('client-layout--native');
      document.documentElement.removeAttribute('data-theme');
    };
  }, []);

  // Тема из нативки (client.theme) → data-theme корня. Класс + атрибут вместе
  // активируют нативную палитру нужной темы (см. styles/theme.scss).
  useEffect(() => {
    if (!isInWebView()) return;
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Питаем сигнал темы для статических antd-тостеров (holderRender в index.tsx):
  // пока клиент смонтирован — его тема, при размонтировании — дефолт antd.
  useEffect(() => {
    setNotifyTheme({ active: true, mode: theme, native: isInWebView() });
    return () => {
      setNotifyTheme({ active: false, mode: 'light', native: false });
    };
  }, [theme]);

  return (
    <ConfigProvider theme={antdThemeConfig}>
      <ClientContext.Provider
        value={{
          filteredQrBrand: filteredQrBrand || null,
          filteredQrMultiBrands: filteredQrMultiBrandsArray || null,
          filteredQrCity: filteredQrCity || null,
          filteredQrDealer: filteredQrDealer || dealer || null
        }}
      >
        <div className="client-layout">
          {hasFullscreenLoading &&
            (fullscreenBlurCount > 0 ? (
              <ClientModuleLoader fullScreen blurMode />
            ) : (
              fullscreenCount > 0 && <ClientModuleLoader fullScreen />
            ))}

          <Header />

          <main
            className={`client-layout__main ${layoutCount > 0 || layoutBlurCount > 0 ? 'client-layout__main--loading' : ''}`}
          >
            {!hasFullscreenLoading &&
              (layoutBlurCount > 0 ? (
                <ClientModuleLoader layout blurMode />
              ) : (
                layoutCount > 0 && <ClientModuleLoader layout />
              ))}

            {dictsLoading ? (
              // Блокируем отображение контента до загрузки словарей.
              // К словарям привязана проверка лизинговых компаний в clientCreateApi.getOnlineDictionaries()
              <ClientModuleLoader layout blurMode />
            ) : (
              <ErrorBoundary
                onError={console.error}
                fallback={
                  <div className="client-layout__error">
                    <ErrorTemplate
                      type="oops"
                      redirect={{
                        text: t('client.go-to-creation'),
                        href: `/${i18n.language}${getFullClientRoute(CLIENT_ROUTES.autoCreditCreate)}`
                      }}
                    />
                  </div>
                }
              >
                <Suspense fallback={<ClientModuleLoader fullScreen />}>
                  <Outlet key={location.pathname} />
                </Suspense>
              </ErrorBoundary>
            )}
          </main>
          <BottomTabBar />
          <ContinueRequest />
        </div>
      </ClientContext.Provider>
    </ConfigProvider>
  );
};

export default ClientLayout;
